import { Search } from 'lucide-preact'
import type { EmailActivityStatusFilter } from '@/types/emailActivity'

const STATUS_CHIPS: { value: EmailActivityStatusFilter; label: string; danger?: boolean }[] = [
    { value: 'upcoming', label: 'Upcoming' },
    { value: 'sent', label: 'Sent' },
    { value: 'delivered', label: 'Delivered' },
    { value: 'opened', label: 'Opened' },
    { value: 'clicked', label: 'Clicked' },
    { value: 'bounced', label: 'Bounced', danger: true },
    { value: 'failed', label: 'Failed', danger: true },
    { value: 'all', label: 'All' },
]

const TYPE_OPTIONS: { value: string | undefined; label: string }[] = [
    { value: undefined, label: 'All types' },
    { value: 'marketing', label: 'Marketing' },
    { value: 'transactional', label: 'Transactional' },
]

interface Props {
    status: EmailActivityStatusFilter
    onStatusChange: (status: EmailActivityStatusFilter) => void
    type: string | undefined
    onTypeChange: (type: string | undefined) => void
    searchInput: string
    onSearchInput: (value: string) => void
}

// The activity list's filter bar: status chips (the primary axis), a Marketing/Transactional type
// segmented control, and recipient search. Fully controlled by the page.
export function EmailActivityFilters({ status, onStatusChange, type, onTypeChange, searchInput, onSearchInput }: Props) {
    return (
        <div class="space-y-3 border-y border-slate-200 px-6 py-3 dark:border-slate-800">
            <div class="flex flex-wrap items-center gap-1.5" role="group" aria-label="Filter by status">
                {STATUS_CHIPS.map(chip => {
                    const active = status === chip.value
                    return (
                        <button key={chip.value} type="button" onClick={() => onStatusChange(chip.value)}
                            class={`rounded-full px-3 py-1 text-sm font-medium ${active
                                ? 'bg-slate-900 text-white dark:bg-white dark:text-slate-900'
                                : chip.danger
                                    ? 'text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/30'
                                    : 'text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800'}`}>
                            {chip.label}
                        </button>
                    )
                })}
            </div>
            <div class="flex flex-wrap items-center gap-3">
                <div class="flex items-center rounded-md border border-slate-200 p-0.5 text-xs dark:border-slate-700" role="group" aria-label="Filter by type">
                    {TYPE_OPTIONS.map(opt => {
                        const active = type === opt.value
                        return (
                            <button key={opt.label} type="button" onClick={() => onTypeChange(opt.value)}
                                class={`rounded px-2.5 py-1 font-medium ${active ? 'bg-slate-900 text-white dark:bg-white dark:text-slate-900' : 'text-slate-500 dark:text-slate-400'}`}>
                                {opt.label}
                            </button>
                        )
                    })}
                </div>
                <div class="relative ml-auto">
                    <Search size={14} class="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                        type="search"
                        value={searchInput}
                        onInput={e => onSearchInput((e.target as HTMLInputElement).value)}
                        placeholder="Search recipient email"
                        class="w-64 rounded-md border border-slate-300 bg-white py-1.5 pl-8 pr-2.5 text-sm text-slate-800 placeholder:text-slate-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200"
                    />
                </div>
            </div>
        </div>
    )
}
